import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../hooks/useAuth";
import useVoiceUtils from "../../hooks/useVoiceUtils";
import useStreamingVOP from "../../hooks/useStreamingVOP";
import { fetchDeepInsightsChatCompletions, getSpeechWsUrl } from "../../store/apiActions";
import { generateSessionId } from "../../utils/commonHelper";
import { encrypted } from "../../utils/utility";
import { SSE_EVENTS } from "../../constant/keyConstants";
import "./VoiceConversationOverlay.css";

/**
 * VoiceConversationOverlay — full-screen "Voice Conversation Mode" (DeepInsights).
 *
 * Real end-to-end flow:
 * - mic capture + STT via useVoiceUtils (same WS used by inline dictate)
 * - DeepInsights streaming agent endpoint (fetchDeepInsightsChatCompletions)
 * - streaming TTS playback via useStreamingVOP (same WS/hook used by MessageBox "Listen")
 *
 * Controlled component: mount once, toggle via `isOpen`/`onClose`.
 *
 * `sessionId` — parent's DeepInsights session id. Reused so voice turns land in
 * the same backend session as text chat.
 * `onClose(turns)` — called when dismissed, with transcript turns:
 *   [{ id, role: "user"|"assistant", text }, ...]
 */

/** Same speed used by the main chat "Listen" button, so this mode sounds identical. */
const VOICE_TTS_SPEED = 1.00;

const STATUS_LABEL = {
  idle: "Ready",
  listening: "Listening",
  thinking: "Preparing…",
  speaking: "Speaking · tap the orb to interrupt",
  reconnecting: "Reconnecting",
  error: "Error",
};

const ERROR_COPY = {
  mic: {
    title: "Microphone unavailable",
    message:
      "We couldn't access your microphone. Check your browser's mic permission for this site and try again.",
  },
  network: {
    title: "Connection lost",
    message:
      "The speech connection dropped. Anything already answered is safe — try again to keep talking.",
  },
  api: {
    title: "DeepInsights couldn't respond",
    message:
      "The request to the agent failed. This is a real backend error, not a simulated one — try again in a moment.",
  },
};

/** Minimal SSE line parser — trimmed to just what this overlay needs. */
function parseSseLines(lines, handlers) {
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || !line.startsWith("data:")) continue;
    let dataStr = line.slice(5).trim();
    if (!dataStr || dataStr === "[DONE]") continue;

    try {
      const decrypted = encrypted.decrypt(dataStr);
      if (decrypted) dataStr = decrypted;
    } catch {
      // Not encrypted / already plain JSON — use dataStr as-is.
    }

    let event;
    try {
      event = JSON.parse(dataStr);
    } catch {
      continue;
    }
    handlers.onEvent(event);
  }
}

/**
 * Thin wrapper around useStreamingVOP so each agent turn gets a brand-new
 * hook instance (remount per turn resets internal per-message refs/WS connection).
 */
const VopPlayer = React.forwardRef(function VopPlayer(
  { content, isPlaceholder, wsUrl, onVoiceOutputStart, onVoiceOutputEnd },
  ref
) {
  const { isVoicePlaying, handleVoiceToggle } = useStreamingVOP({
    content,
    isPlaceholder,
    enableVoiceOutput: true,
    autoPlayVoice: true,
    isAssistant: true,
    isStreamingVop: true,
    wsUrl,
    onVoiceOutputStart,
    onVoiceOutputEnd,
  });

  useImperativeHandle(
    ref,
    () => ({
      isPlaying: () => isVoicePlaying,
      interrupt: () => {
        if (isVoicePlaying) handleVoiceToggle();
      },
    }),
    [isVoicePlaying, handleVoiceToggle]
  );

  return null;
});

const VoiceConversationOverlay = ({ isOpen, onClose, sessionId }) => {
  const { username, ugdnId } = useAuth();

  const [phase, setPhase] = useState("idle"); // idle | listening | thinking | speaking | reconnecting | error
  const [caption, setCaption] = useState("");
  const [note, setNote] = useState("");
  const [errorKind, setErrorKind] = useState("network");
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // PTT-only: permanently passive (toggle removed per requirement).
  const [conversationMode] = useState("passive");

  const [turns, setTurns] = useState([]);
  const [silenceRestartTick, setSilenceRestartTick] = useState(0);

  const [vopTurn, setVopTurn] = useState(0);
  const [vopContent, setVopContent] = useState("");
  const [vopIsPlaceholder, setVopIsPlaceholder] = useState(true);

  const phaseRef = useRef(phase);
  const mutedRef = useRef(muted);
  const conversationModeRef = useRef(conversationMode);
  const endedRef = useRef(false);
  const phaseStartRef = useRef(Date.now());
  const abortControllerRef = useRef(null);
  const lastQueryRef = useRef("");
  const vopRef = useRef(null);
  const ttsWsUrl = useRef(getSpeechWsUrl("tts", "deepInsights", { speed: VOICE_TTS_SPEED })).current;
  const turnIdRef = useRef(0);
  const transcriptScrollRef = useRef(null);
  const nextTurnId = useCallback(() => `t${Date.now()}_${turnIdRef.current++}`, []);

  const fallbackSessionIdRef = useRef(null);
  const resolveSessionId = useCallback(() => {
    if (sessionId) return sessionId;
    if (!fallbackSessionIdRef.current) fallbackSessionIdRef.current = generateSessionId();
    return fallbackSessionIdRef.current;
  }, [sessionId]);

  const turnsRef = useRef(turns);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { conversationModeRef.current = conversationMode; }, [conversationMode]);
  useEffect(() => { turnsRef.current = turns; }, [turns]);

  const setPhaseTimed = useCallback((next) => {
    phaseStartRef.current = Date.now();
    setPhase(next);
  }, []);

  // Live elapsed timer (matches prototype)
  useEffect(() => {
    if (!isOpen) return undefined;
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - phaseStartRef.current) / 1000));
    }, 250);
    return () => clearInterval(iv);
  }, [isOpen, phase]);

  // Keep transcript scrolled
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, caption]);

  const goError = useCallback((kind) => {
    setErrorKind(kind);
    setPhaseTimed("error");
  }, [setPhaseTimed]);

  // ── Real STT ────────────────────────────────────────────────────────────
  const { startListening, stopListening, status: sttStatus } = useVoiceUtils({
    enabled: true,
    wsUrl: getSpeechWsUrl("transcribe", "deepInsights"),
    // DeepInsights STT expects an explicit first-message auth envelope even when token is null.
    // Keep this behavior DeepInsights-only so other apps are not affected.
    sendNullTokenOnOpen: true,
    backgroundMode: conversationMode === "active" && phase === "speaking",
    noiseGateThreshold: 0.045,
    speakingNoiseGateThreshold: 0.11,
    autoSendDelayMs: 2600,
    onPartial: (text) => {
      if (phaseRef.current === "listening") setCaption(text);
    },
    onSilenceAutoSend: (text) => {
      const finalText = (text || "").trim();
      if (finalText) {
        handleAsk(finalText);
      } else if (!endedRef.current && !mutedRef.current && phaseRef.current === "listening") {
        if (conversationModeRef.current === "active") {
          setSilenceRestartTick((t) => t + 1);
        } else {
          setPhaseTimed("idle");
        }
      }
    },
    handleStopStreaming: () => {
      vopRef.current?.interrupt();
    },
    onKeyword: () => {
      if (endedRef.current || mutedRef.current) return;
      if (phaseRef.current === "speaking") {
        setCaption("");
        setPhaseTimed("listening");
      }
    },
    onError: () => {
      if (!endedRef.current) goError("mic");
    },
    onStatusChange: (next) => {
      if (endedRef.current) return;
      if (next === "error") goError("mic");
      if (next === "disconnected" && phaseRef.current === "listening") goError("network");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // ── Real agent call + real TTS playback ─────────────────────────────────
  const handleAsk = useCallback(async (userText) => {
    if (!userText || endedRef.current) return;
    lastQueryRef.current = userText;
    stopListening();
    setCaption("");
    setNote("");
    setPhaseTimed("thinking");

    const assistantTurnId = nextTurnId();
    setTurns((prev) => [
      ...prev,
      { id: nextTurnId(), role: "user", text: userText },
      { id: assistantTurnId, role: "assistant", text: "" },
    ]);

    // New turn → fresh VopPlayer instance
    setVopTurn((t) => t + 1);
    setVopContent("");
    setVopIsPlaceholder(true);

    const session = resolveSessionId();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let streamedText = "";
    let finalResponse = null;
    let isStreamComplete = false;

    const onEvent = (event) => {
      const type = event?.type;
      const data = event?.data || event || {};
      if (type === SSE_EVENTS.PROGRESS) {
        const msg = data?.message || data?.text || data?.content;
        if (msg && !streamedText) setNote(msg);
      } else if (type === SSE_EVENTS.TOKEN) {
        const tokenText = data?.delta || data?.token || data?.text || data?.content || "";
        if (tokenText) {
          streamedText += tokenText;
          setTurns((prev) => prev.map((t) => (t.id === assistantTurnId ? { ...t, text: streamedText } : t)));
          setVopContent(streamedText);
        }
      } else if (type === SSE_EVENTS.FINAL) {
        finalResponse = data;
      } else if (type === SSE_EVENTS.STREAM_COMPLETE) {
        isStreamComplete = true;
      } else if (type === SSE_EVENTS.ERROR) {
        throw new Error(data?.message || "Agent error");
      }
    };

    try {
      const response = await fetchDeepInsightsChatCompletions(
        {
          user_id: username,
          ugdn_id: ugdnId || "",
          session_id: session,
          query: userText,
          stream: true,
          voice_mode: true,
        },
        abortController
      );

      if (!response?.ok || !response.body) throw new Error("Streaming failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let leftover = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        const combined = leftover + chunk;
        const lines = combined.split("\n");
        leftover = lines.pop() || "";
        if (done && leftover) {
          lines.push(leftover);
          leftover = "";
        }
        parseSseLines(lines, { onEvent });
        if (done || isStreamComplete) break;
      }

      if (endedRef.current) return;

      const finalContent =
        finalResponse?.response ||
        finalResponse?.text ||
        finalResponse?.content ||
        streamedText ||
        "Sorry, I couldn't generate a response. Please try again.";

      setTurns((prev) => prev.map((t) => (t.id === assistantTurnId ? { ...t, text: finalContent } : t)));
      setNote("");

      setVopContent(finalContent);
      setVopIsPlaceholder(false);
    } catch (error) {
      if (error?.name === "AbortError" || endedRef.current) return;
      console.error("VoiceConversationOverlay agent error:", error);
      goError("api");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, ugdnId, stopListening, startListening, goError, setPhaseTimed, nextTurnId, resolveSessionId]);

  const closeOverlay = useCallback(() => {
    endedRef.current = true;
    abortControllerRef.current?.abort();
    stopListening();
    vopRef.current?.interrupt();
    setPhaseTimed("idle");
    setCaption("");
    setNote("");
    onClose?.(turnsRef.current);
  }, [stopListening, setPhaseTimed, onClose]);

  useEffect(() => {
    if (isOpen) {
      endedRef.current = false;
      setMuted(false);
      setCaption("");
      setNote("");
      setTurns([]);
      // Passive/PTT: do NOT auto-start mic.
      setPhaseTimed("idle");
      return undefined;
    }
    endedRef.current = true;
    abortControllerRef.current?.abort();
    stopListening();
    vopRef.current?.interrupt();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Re-arm after silence timer fired with nothing actually said
  useEffect(() => {
    if (silenceRestartTick === 0) return;
    if (!isOpen || endedRef.current || mutedRef.current) return;
    if (phaseRef.current !== "listening") return;
    startListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silenceRestartTick]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      if (next && (phaseRef.current === "listening" || phaseRef.current === "speaking")) {
        stopListening();
        setNote("Mic is muted");
      } else if (!next && phaseRef.current !== "thinking") {
        setNote("");
        if (phaseRef.current === "listening") setPhaseTimed("idle");
      }
      return next;
    });
  }, [stopListening, setPhaseTimed]);

  const handlePttTap = useCallback(() => {
    if (endedRef.current || mutedRef.current) return;
    if (phase === "speaking") vopRef.current?.interrupt();
    if (phase === "thinking") abortControllerRef.current?.abort();
    setCaption("");
    setPhaseTimed("listening");
    stopListening();
    startListening();
  }, [phase, stopListening, startListening, setPhaseTimed]);

  const handleOrbClick = useCallback(() => {
    if (phase === "error") {
      setPhaseTimed("listening");
      startListening();
      return;
    }
    if (conversationMode === "passive") {
      handlePttTap();
      return;
    }
    if (phase === "speaking") {
      vopRef.current?.interrupt();
      setCaption("");
      setPhaseTimed("listening");
      startListening();
    }
  }, [phase, conversationMode, handlePttTap, startListening, setPhaseTimed]);

  const handleMicButtonClick = useCallback(() => {
    if (conversationMode === "passive") {
      if (phase === "listening") {
        stopListening();
        setCaption("");
        setPhaseTimed("idle");
        return;
      }
      handlePttTap();
      return;
    }
    toggleMute();
  }, [conversationMode, phase, handlePttTap, stopListening, setPhaseTimed, toggleMute]);

  const retryFromError = useCallback(() => {
    if (errorKind === "api" && lastQueryRef.current) {
      handleAsk(lastQueryRef.current);
    } else {
      setPhaseTimed("listening");
      startListening();
    }
  }, [errorKind, handleAsk, startListening, setPhaseTimed]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) closeOverlay();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOverlay]);

  useEffect(() => () => {
    endedRef.current = true;
    stopListening();
    vopRef.current?.interrupt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;

  const timerLabel = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  const errorCopy = ERROR_COPY[errorKind] || ERROR_COPY.network;
  const captionDim = phase === "listening" && !caption;
  const phaseLabel = phase === "idle" && conversationMode === "passive" ? "Tap to talk" : STATUS_LABEL[phase];

  return createPortal(
    <div className="vco-root" data-vp={phase} role="dialog" aria-modal="true" aria-label="Conversation with DeepInsights">
      <div className="vco-bg"><i></i><i></i><i></i></div>

      <div className="vco-center">
        <button className="vco-orbw" onClick={handleOrbClick} aria-label="Interrupt / tap to speak">
          <span className="vco-orb"></span>
        </button>

        <VopPlayer
          key={vopTurn}
          ref={vopRef}
          content={vopContent}
          isPlaceholder={vopIsPlaceholder}
          wsUrl={ttsWsUrl}
          onVoiceOutputStart={() => {
            if (endedRef.current) return;
            setPhaseTimed("speaking");
          }}
          onVoiceOutputEnd={() => {
            if (endedRef.current) return;
            if (phaseRef.current !== "speaking") return;
            // Passive/PTT: never auto-resume listening — wait for next explicit tap.
            setPhaseTimed("idle");
            stopListening();
          }}
        />

        {phase !== "error" && (
          <>
            {turns.length === 0 ? (
              <div className={`vco-cap ${captionDim ? "dim" : ""}`} aria-live="polite">
                {caption || (phase === "listening" ? "Listening…" : phaseLabel)}
              </div>
            ) : (
              <div className="vco-transcript" ref={transcriptScrollRef} aria-live="polite">
                {turns.map((t) => (
                  <div key={t.id} className={`vco-msg vco-msg-${t.role}`}>
                    {t.text || (t.role === "assistant" ? "…" : "")}
                  </div>
                ))}
                {phase === "listening" && caption && (
                  <div className="vco-msg vco-msg-user vco-msg-ghost">{caption}</div>
                )}
              </div>
            )}
            <div className="vco-state">
              <span>{phaseLabel}</span>
              <span className="vco-timer">{timerLabel}</span>
            </div>
            {note && <div className="vco-note">{note}</div>}
            {!note && sttStatus === "connecting" && phase === "listening" && (
              <div className="vco-note">Connecting microphone…</div>
            )}
          </>
        )}

        {phase === "error" && (
          <div className="vco-err" role="alert">
            <div className="vco-etitle">{errorCopy.title}</div>
            <div className="vco-etext">{errorCopy.message}</div>
            <div className="vco-eact">
              <button className="vco-btn-primary" onClick={retryFromError}>Try again</button>
              <button className="vco-btn-secondary" onClick={closeOverlay}>Close</button>
            </div>
          </div>
        )}
      </div>

      <div className="vco-bar">
        {/* Mode toggle removed — permanently Passive (push-to-talk) only. */}
        <button
          className={`vco-cb ${conversationMode === "active" && muted ? "muted" : ""} ${conversationMode === "passive" && phase === "listening" ? "listening" : ""}`}
          onClick={handleMicButtonClick}
          disabled={conversationMode === "passive" && muted}
          aria-label={phase === "listening" ? "Stop talking" : "Tap to talk"}
          title={phase === "listening" ? "Tap to stop talking" : "Push to talk — tap to speak (works while thinking or replying too)"}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="12" rx="3"></rect>
            <path d="M19 10a7 7 0 0 1-14 0M12 17v5"></path>
          </svg>
        </button>
        <button className="vco-cb vco-cb-end" onClick={closeOverlay} aria-label="End conversation" title="End conversation">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

export default VoiceConversationOverlay;

